import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';

import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';

import useSound from 'use-sound';

import { useSearchParams } from 'react-router-dom';

import api from '../services/apiClient';

import IParametroAparencia from '../interfaces/IParametroAparencia';
import notificationSound from '../assets/sound/notification-mensage.mp3';
import notificationStartChat from '../assets/sound/notification-start-chat.wav';

interface AuthState {
  token?: string;
  user?: string;
  nameClient: string;
  hubConnection?: HubConnection | undefined;
  position?: number;
  setPosition?: (position: number) => void;
  playSoundNotification?: () => void;
  playSoundNotificationStartChat?: () => void;
}

interface AuthContextData {
  dataConnection: AuthState;
  signIn(data: AuthState): Promise<void>;
  signOut(): void;
  idRequestSupport: string | undefined;
  setIdRequestSupport(id: string | undefined): void;
  position: number;
  setPosition(position: number): void;
  playSoundNotification: () => void;
  playSoundNotificationStartChat: () => void;
  loadLogoEmpresaCliente(link: string): void;
  urlLogoEmpresaCliente: string;
  urlBackgroundImage: string;
  loadConfigAparrence(link: string): void;
  configAparrence: IParametroAparencia | undefined;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const AuthProvider: React.FC = ({ children }) => {
  const [hubConn, sethubConnection] = useState<HubConnection>();
  const [idRequestSupport, setIdRequestSupport] = useState<string>();
  const [position, setPosition] = useState<number>(0);
  const [urlLogoEmpresaCliente, setUrlLogoEmpresaCliente] = useState('');
  const [urlBackgroundImage, setUrlBackgroundImage] = useState('');
  const [playSoundNotification] = useSound(notificationSound);
  const [playSoundNotificationStartChat] = useSound(notificationStartChat);
  const [configAparrence, setConfigAparrence] = useState<IParametroAparencia>();

  const [searchParams] = useSearchParams();

  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@LiveChat:token');
    const user = localStorage.getItem('@LiveChat:user');
    const nameClient = localStorage.getItem('@LiveChat:nameClient');

    if (token && user && hubConn && nameClient) {
      return { token, user, hubConn, nameClient };
    }

    return {} as AuthState;
  });

  useEffect(() => {
    if (idRequestSupport) {
      localStorage.setItem(
        '@LiveChat:seqAtendimento',
        String(idRequestSupport)
      );
    }
  }, [idRequestSupport]);

  const signIn = useCallback(async ({ nameClient }) => {
    const conn = new HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_URL}/chat`)
      .configureLogging(LogLevel.Information)
      .build();

    await conn.start();

    conn.onclose(() => {
      sethubConnection({} as HubConnection);
      setData({
        token: '',
        user: '',
        nameClient: '',
        hubConnection: hubConn,
      });
    });

    sethubConnection(conn);

    let link = String(searchParams.get('emp')).toUpperCase();

    if (link === '' || link === 'NULL')
      link = String(localStorage.getItem('@LiveChat:linkEmpresa'));

    const dataPost = {
      userId: '',
      password: '',
      accessKey: 'LiveChat',
      grantType: 'without_password',
      email: '',
      refreshToken: '',
      userName: '',
      connectionId: conn.connectionId,
      linkEmpresa: link,
    };

    const responseLogin: any = await api.post('/login', dataPost);
    const { accessToken } = responseLogin.data;

    localStorage.setItem('@LiveChat:token', accessToken);
    localStorage.setItem('@LiveChat:nameClient', nameClient);
    localStorage.setItem('@LiveChat:connectionId', conn.connectionId as string);
    localStorage.setItem('@LiveChat:linkEmpresa', link);
    setData({
      token: accessToken,
      user: '',
      nameClient,
      hubConnection: conn,
    });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@LiveChat:token');
    localStorage.removeItem('@LiveChat:nameClient');

    setData({} as AuthState);
  }, []);

  const loadLogoEmpresaCliente = useCallback(
    async (link: string): Promise<void> => {
      api.get(`/parametro/cliente/${link}`).then((response) => {
        const result = response.data;

        if (result.success) {
          setUrlLogoEmpresaCliente(
            `${process.env.REACT_APP_API_URL}/LogoEmpresa/${result.data.seqEmpresa}/${result.data.nomeFileLogoCliente}`
          );
        }
      });

      api.get(`/parametro/parametroAparencia/${link}`).then((response) => {
        const result = response.data;
        if (result.success) {
          setConfigAparrence(result.data);
        }
      });
    },
    []
  );

  const loadConfigAparrence = useCallback(
    async (link: string): Promise<void> => {
      api.get(`/parametro/parametroAparencia/${link}`).then((response) => {
        const result = response.data;
        if (result.success) {
          setConfigAparrence(result.data);
        }
      });
    },
    []
  );

  const valueContext = useMemo(
    () => ({
      dataConnection: data,
      signIn,
      signOut,
      idRequestSupport,
      setIdRequestSupport,
      position,
      setPosition,
      playSoundNotification,
      playSoundNotificationStartChat,
      loadLogoEmpresaCliente,
      urlLogoEmpresaCliente,
      urlBackgroundImage,
      loadConfigAparrence,
      configAparrence,
    }),
    [
      data,
      signIn,
      signOut,
      idRequestSupport,
      setIdRequestSupport,
      position,
      setPosition,
      playSoundNotification,
      playSoundNotificationStartChat,
      loadLogoEmpresaCliente,
      urlLogoEmpresaCliente,
      urlBackgroundImage,
      loadConfigAparrence,
      configAparrence,
    ]
  );

  return (
    <AuthContext.Provider value={valueContext}>{children}</AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
